import { useEffect } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { useQueryClient } from '@tanstack/react-query';
import { getFirebaseMessaging } from '../lib/firebase';
import { registerFCMToken } from '../api/notifications';
import { NOTIFICATIONS_KEY, UNREAD_COUNT_KEY } from './useNotifications';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const FCM_TOKEN_KEY = 'fcm_token';

const useFCM = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    let unsubscribe;

    const setup = async () => {
      try {
        const messaging = await getFirebaseMessaging();
        if (!messaging) return;

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const existing = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (!existing) {
          await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }
        const swReg = await navigator.serviceWorker.ready;

        const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
        if (!token) return;

        const stored = localStorage.getItem(FCM_TOKEN_KEY);
        if (stored !== token) {
          await registerFCMToken(token);
          localStorage.setItem(FCM_TOKEN_KEY, token);
        }

        unsubscribe = onMessage(messaging, (payload) => {
          const title = payload.notification?.title || 'HostAssist';
          const body  = payload.notification?.body  || '';
          // Must use swReg.showNotification() — new Notification() is blocked
          // by Chrome when a service worker controls the page
          swReg.showNotification(title, { body, icon: '/logo.png' });

          // Invalidate the in-app feed so the bell badge + notifications
          // screen stay in sync with what the device just received.
          queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
          queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
        });
      } catch (err) {
        console.error('[FCM] setup error:', err);
      }
    };

    setup();
    return () => unsubscribe?.();
  }, [queryClient]);
};

export default useFCM;
